import { AlertTriangle, CheckCircle2, Clock3, Loader2 } from "lucide-react";
import { useNavigate } from "react-router-dom";

import { useOperatorStatus } from "@/hooks/useOperatorStatus";
import { cn } from "@/lib/utils";

export function OperatorStatusStrip() {
  const navigate = useNavigate();
  const statusQ = useOperatorStatus();
  const status = statusQ.data;
  const health = statusQ.isLoading && !status ? "loading" : status?.health ?? "error";

  const Icon =
    health === "loading"
      ? Loader2
      : health === "ok"
        ? CheckCircle2
        : AlertTriangle;

  return (
    <button
      type="button"
      onClick={() => navigate("/operator/status")}
      className={cn(
        "flex h-8 shrink-0 items-center gap-2 rounded-md border px-2.5 text-xs transition-colors",
        statusClass(health),
      )}
      title="Open operator status"
    >
      <Icon
        className={cn("size-3.5", health === "loading" && "animate-spin")}
        aria-hidden
      />
      <span className="hidden font-medium md:inline">{statusLabel(health)}</span>
      <span className="hidden text-muted-fg lg:inline">
        {status?.active_schedules ?? 0} workers
      </span>
      {(status?.recent_failures ?? 0) > 0 ? (
        <span className="hidden text-red-300 sm:inline">{status?.recent_failures} fail</span>
      ) : null}
      {(status?.paused_sources ?? 0) > 0 ? (
        <span className="hidden text-amber-300 sm:inline">{status?.paused_sources} paused</span>
      ) : null}
      {status?.next_run_at ? (
        <span className="hidden items-center gap-1 text-muted-fg xl:inline-flex">
          <Clock3 className="size-3" aria-hidden />
          {relativeTime(status.next_run_at)}
        </span>
      ) : null}
    </button>
  );
}

function statusClass(status: "ok" | "warn" | "error" | "loading"): string {
  switch (status) {
    case "ok":
      return "border-emerald-500/30 bg-emerald-500/10 text-emerald-200 hover:bg-emerald-500/15";
    case "warn":
      return "border-amber-500/35 bg-amber-500/10 text-amber-200 hover:bg-amber-500/15";
    case "error":
      return "border-red-500/35 bg-red-500/10 text-red-200 hover:bg-red-500/15";
    case "loading":
      return "border-border bg-bg/60 text-muted-fg hover:bg-muted/40";
  }
}

function statusLabel(status: "ok" | "warn" | "error" | "loading"): string {
  switch (status) {
    case "ok":
      return "Ops ok";
    case "warn":
      return "Ops warn";
    case "error":
      return "Ops issue";
    case "loading":
      return "Ops";
  }
}

function relativeTime(value: string): string {
  const seconds = Math.round((new Date(value).getTime() - Date.now()) / 1000);
  const absolute = Math.abs(seconds);
  if (absolute < 60) return seconds >= 0 ? "<1m" : "now";
  const minutes = Math.round(absolute / 60);
  if (minutes < 60) return seconds >= 0 ? `${minutes}m` : `${minutes}m ago`;
  const hours = Math.round(minutes / 60);
  return seconds >= 0 ? `${hours}h` : `${hours}h ago`;
}
